import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Badge } from '@/ui/components/badge';
import { Button } from '@/ui/components/button';
import { Input } from '@/ui/components/input';
import { ChevronRightIcon, HelpIcon, RefreshIcon } from '@/ui/icons';
import IncomeModal from './incomeModal';
import { type AmountFieldController, useAmountField } from './useAmountField';
import AngelTaxResultView from './angelTaxResultView';
import NewsPicksBanner, {
  type NewsPicksBannerProps,
} from '../common/newsPicksBanner';
import {
  calculateAngelTax,
  type AngelTaxResult,
} from '../../models/questions/angelTaxCalculator';

interface Props {
  taxReturnImage: string;
  taxWithholdingImage: string;
  // 「案件一覧を見る」の遷移先（案件一覧の「全て」）
  projectsPath: string;
  // 配信期間外は null
  newsPicksBanner: NewsPicksBannerProps | null;
}

interface AmountFieldProps {
  id: string;
  label: string;
  placeholder: string;
  field: Pick<AmountFieldController, 'required' | 'error' | 'inputProps'>;
  // ラベル横の補助（総所得金額の「？」ボタン）
  labelAddon?: React.ReactNode;
  // 入力欄の下に出す注記
  notes?: string[];
}

function AmountField({
  id,
  label,
  placeholder,
  field: { required, error, inputProps },
  labelAddon,
  notes,
}: AmountFieldProps) {
  return (
    // 1 行目は上の罫線を持たない。SP は上の余白もなし
    <div className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:py-4 tw:first:border-t-0 tw:first:pt-0 tw:md:py-3 tw:md:first:pt-3">
      <div className="tw:flex tw:flex-col tw:gap-2 tw:md:flex-row tw:md:items-center tw:md:justify-between">
        <div className="tw:flex tw:items-center tw:gap-2 tw:md:gap-3">
          <label
            htmlFor={id}
            className="tw:m-0 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:leading-none tw:font-semibold tw:md:gap-3 tw:md:text-base"
          >
            <Badge
              variant={required ? 'default' : 'secondary'}
              className="tw:font-noto"
            >
              {required ? '必須' : '任意'}
            </Badge>
            {label}
          </label>
          {labelAddon}
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Input
            id={id}
            type="text"
            inputMode="numeric"
            className="tw:w-full tw:text-right tw:md:w-43"
            placeholder={placeholder}
            {...inputProps}
          />
          <span className="tw:text-sm tw:leading-none tw:font-semibold tw:tracking-text tw:md:text-base">
            円
          </span>
        </div>
      </div>
      {error != null && (
        <p
          role="alert"
          className="tw:m-0 tw:text-sm tw:font-semibold tw:text-destructive tw:md:text-right"
        >
          {error}
        </p>
      )}
      {notes != null && (
        <ul className="tw:m-0 tw:list-none tw:p-0 tw:font-noto tw:text-2xs tw:leading-normal tw:tracking-text tw:text-muted-foreground">
          {notes.map(note => (
            <li key={note}>※{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AngelTaxView({
  taxReturnImage,
  taxWithholdingImage,
  projectsPath,
  newsPicksBanner,
}: Props) {
  const income = useAmountField({ required: true });
  const investment = useAmountField({ required: true });
  const gain = useAmountField({ required: false });
  const gainByOutsideStock = useAmountField({ required: false });
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [result, setResult] = useState<AngelTaxResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // 結果セクションの上端がヘッダーの直下に来る位置まで送る。ヘッダーは sticky で React の外にあるので高さは実測する
  const scrollToResult = () => {
    if (resultRef.current == null) return;
    const headerHeight =
      document.getElementById('g-header')?.getBoundingClientRect().height ?? 0;
    window.scrollTo({
      top:
        resultRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerHeight,
      behavior: 'smooth',
    });
  };

  const handleSubmit = () => {
    const fields = [income, investment, gain, gainByOutsideStock];
    const requiredEmptyFields = fields.filter(field => field.isRequiredEmpty);
    requiredEmptyFields.forEach(field => field.showRequiredError());
    // 未入力や入力エラーの欄があるうちは計算しない（表示中の結果は変えない）
    if (
      requiredEmptyFields.length > 0 ||
      fields.some(field => field.error != null)
    ) {
      return;
    }

    // 譲渡益は任意で、空は 0 として計算する。何で計算したかが分かるように入力欄にも 0 を補完する
    gain.fillZeroIfEmpty();
    gainByOutsideStock.fillZeroIfEmpty();
    // 結果を描画してからその位置へ送るため、state の反映を同期的に済ませる
    flushSync(() => {
      setResult(
        calculateAngelTax({
          income: income.amount,
          investment: investment.amount,
          gain: gain.amount,
          gainByOutsideStock: gainByOutsideStock.amount,
        }),
      );
    });
    scrollToResult();
  };

  return (
    <div className="ui-root tw:font-hiragino">
      {/* 上下の余白はこのページの余白を全部ここで持つ（レガシー SCSS 側は reboot の既定を 0 にしているだけ）。
          左右は SP で Figma の 20px。レイアウトの .container が持つ 15px を負のマージンで打ち消してから取る */}
      <div className="tw:-mx-3.75 tw:flex tw:flex-col tw:items-center tw:gap-11 tw:px-5 tw:pt-10 tw:pb-10 tw:md:mx-0 tw:md:gap-12 tw:md:px-0 tw:md:pt-20 tw:md:pb-15">
        {/* NewsPicks 番組バナー（配信期間中のみ）。タイトルとの間隔は親の gap に任せる */}
        {newsPicksBanner != null && (
          <NewsPicksBanner banner={newsPicksBanner} />
        )}
        <div className="tw:flex tw:w-full tw:max-w-150 tw:flex-col tw:items-center tw:gap-4">
          <h1 className="tw:m-0 tw:text-center tw:text-xl tw:leading-normal tw:font-semibold tw:tracking-text tw:md:text-4xl">
            エンジェル税制シミュレーター
          </h1>
          <p className="tw:m-0 tw:text-center tw:font-noto tw:text-(length:--angel-tax-text-note) tw:leading-normal tw:md:text-base">
            エンジェル税制には、優遇措置A、優遇措置B、プレシード・シード特例があり、
            <br className="tw:hidden tw:md:inline" />
            所得控除や株式売却益からの控除などそれぞれ受けられる税制優遇が異なります。
          </p>
        </div>
        <div className="tw:flex tw:w-full tw:max-w-150 tw:flex-col tw:items-center tw:gap-11 tw:md:gap-12">
          <form
            className="tw:m-0 tw:flex tw:w-full tw:flex-col tw:gap-6 tw:md:gap-10"
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="tw:flex tw:flex-col">
              <AmountField
                id="income"
                label="総所得金額"
                placeholder="例, 10000000"
                field={income}
                labelAddon={
                  // Preflight を読み込んでいないため button の UA 既定（枠線・背景）を打ち消す
                  <button
                    type="button"
                    aria-label="総所得金額とは？"
                    className="tw:m-0 tw:flex tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-input-icon"
                    onClick={() => setShowIncomeModal(true)}
                  >
                    <HelpIcon className="tw:size-6" />
                  </button>
                }
              />
              <AmountField
                id="investment"
                label="エンジェル税制適用会社への投資額"
                placeholder="例, 500000"
                field={investment}
              />
              <AmountField
                id="gain"
                label="上場株式の売買による譲渡益"
                placeholder="例, 1000000"
                field={gain}
                notes={[
                  '上場株式の売買において譲渡損の場合は0を記入してください。',
                  '上場株式に関わる譲渡損失の繰越がない前提で記入してください。',
                  '株式譲渡は特定口座（源泉あり）で取引されていることが前提です。',
                ]}
              />
              <AmountField
                id="gain-by-outside-stock"
                label="非上場株式の売買による譲渡益"
                placeholder="例, 1000000"
                field={gainByOutsideStock}
                notes={[
                  '非上場株式の売買において譲渡損の場合は0を記入してください。',
                  '非上場株式に関わる譲渡損失の繰越がない前提で記入してください。',
                ]}
              />
            </div>
            {/* 計算するのシェブロンだけ右端に固定する（data-icon） */}
            {result != null ? (
              <Button
                type="submit"
                size="lg"
                variant="outline"
                className="tw:mx-auto tw:h-13 tw:w-65 tw:font-noto tw:md:h-15 tw:md:w-81"
              >
                再度シミュレーションする
                <RefreshIcon />
              </Button>
            ) : (
              <Button
                type="submit"
                size="lg"
                className="tw:mx-auto tw:h-13 tw:w-65 tw:font-noto tw:md:h-15 tw:md:w-81"
              >
                計算する
                <ChevronRightIcon data-icon="end" />
              </Button>
            )}
          </form>
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:text-xs tw:tracking-text tw:text-muted-foreground tw:md:gap-1">
            <p className="tw:m-0 tw:font-noto tw:leading-none tw:font-bold">
              所得税の計算について
            </p>
            <p className="tw:m-0 tw:leading-normal">
              ※令和7年12月時点の税制を基にした概算のシミュレーションとなります。所得控除は基礎控除のみで、その他控除については考慮していません。所得税額には、復興特別所得税額(2.1%)が含まれております。株式譲渡は分離課税選択で税率は15.315%(所得税および復興特別所得税)です。基礎控除の計算については、総所得金額と合計所得金額が同一である前提で行っております。
            </p>
          </div>
        </div>
      </div>

      <div ref={resultRef}>
        <AngelTaxResultView result={result} projectsPath={projectsPath} />
      </div>

      <IncomeModal
        show={showIncomeModal}
        taxReturnImage={taxReturnImage}
        taxWithholdingImage={taxWithholdingImage}
        onClose={() => setShowIncomeModal(false)}
      />
      <div className="angel-tax__cta">
        <div className="angel-tax__cta__inner">
          <div className="angel-tax__cta__text">
            最短３分！WEBで完結！
            <br />
            登録後、新しい案件情報をお届けします。
          </div>
          <a href="/users/sign_up" className="angel-tax__cta__btn">
            無料で投資家登録
          </a>
        </div>
      </div>
    </div>
  );
}
